import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { has, get } from "lodash";
import {
  Home as HomeIcon,
  Group as GroupIcon,
  //Call as CallIcon,
  Info as InfoIcon,
  NavigateNext as NavigateNextIcon,
  InsertDriveFile as InsertDriveFileIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";

/**
 * Site Sidebar
 * @returns {JSX.Element}
 * @component
 */
const Sidebar = () => {
  const [activeMenus, setActiveMenus] = useState("home");
  // eslint-disable-next-line no-unused-vars
  let navigate = useNavigate();

  const handleclickmenu = (e, x) => {
    switch (e) {
      case "home":
      case "about":
      case "committees":
      case "resources":
        // Currently nothing - make sure to add entries here for items that should
        // expand the menu, but not navigate to a page.  Otherwise an auth redirect occurs
        break;
      case "membership":
        navigate("/members");
        break;
      case "meeting":
        navigate("/meeting-registrants");
        break;
      default:
        navigate(`/${e}`);
        break;
    }

    if (x === true) {
      const isOpening = activeMenus !== e;
      setActiveMenus(isOpening ? e : null);

      if (isOpening && e === "membership") {
        setActiveSubMenus("members");
      }

      if (isOpening && e === "meeting") {
        setActiveSubMenus("meeting-registrants");
      }
    }
  };

  const [activeSubMenus, setActiveSubMenus] = useState("");

  const handleclicksubmenu = (e) => {
    // Most items direct to their name with a prepended forward slash
    switch (e) {
      case "homebanner":
        navigate(`/home`);
        break;
      case "join":
        // Home: Left/Right boxes
        navigate(`/joinsmfa`);
        break;
      case "aboutbanner":
        navigate(`/about`);
        break;
      case "meetingbanner":
        navigate(`/meeting`);
        break;
      default:
        navigate(`/${e}`);
        break;
    }
    setActiveSubMenus(e);
  };

  // Cache for active menu to prevent repeated calculations and log spam
  const activeMenuCache = {
    currentPath: null,
    result: null,
  };

  // Rename to getActiveMenus to avoid conflict with state variable
  // eslint-disable-next-line no-unused-vars
  const getActiveMenus = (path) => {
    // Use cached result if path hasn't changed
    if (activeMenuCache.currentPath === path) {
      return activeMenuCache.result;
    }

    // Otherwise calculate and cache
    let result = [];

    // Original logic to determine active menus
    if (path === "/home") {
      result = ["home"];
    }
    // ... other path checks ...

    // Store in cache
    activeMenuCache.currentPath = path;
    activeMenuCache.result = result;

    // Limit logging frequency to reduce console spam
    if (Math.random() < 0.1) {
      // Only log ~10% of calls
      log.debug("activeMenus", path, result);
    }

    return result;
  };

  const authCheckCompletedRef = useRef(false);

  // Replace the problematic useEffect with a fixed version
  useEffect(() => {
    // CRITICAL FIX: Only run menu setup once
    if (authCheckCompletedRef.current) return;
    authCheckCompletedRef.current = true;

    // Menu active state setup - keep this part
    const locName = window.location.pathname.split("/")[1];

    const menuActiveMap = {
      home: { main: "home", sub: "homebanner" },
      about: { main: "about", sub: "aboutbanner" },
      mission: { main: "home", sub: "mission" },
      // Home: Left/Right boxes:
      joinsmfa: { main: "home", sub: "join" },
      news: { main: "home", sub: "news" },
      getinvolve: { main: "home", sub: "getinvolve" },
      contentmanagement: { main: "about", sub: "contentmanagement" },
      directors: { main: "about", sub: "directors" },
      story: { main: "about", sub: "story" },
      meeting: { main: "meeting", sub: "meetingbanner" },
      meetings: { main: "meeting", sub: "meetings" },
      registeredmeetings: { main: "meeting", sub: "meeting-registrants" },
      benefitsmembership: { main: "membership", sub: "benefitsmembership" },
      memberhighlight: { main: "membership", sub: "memberhighlight" },
      membershipcategories: { main: "membership", sub: "membershipcategories" },
      getinvolved: { main: "membership", sub: "getinvolvedmembership" },
      patients: { main: "patients" },
      //contact: { main: "contact" },
      site: { main: "site" },
      // affiliates: { main: "affiliates" },
      educationmeetingplanningcommittee: {
        main: "committees",
        sub: "educationmeetingplanningcommittee",
      },
      financemembershipcommittee: {
        main: "committees",
        sub: "financemembershipcommittee",
      },
      researchcommittee: { main: "committees", sub: "researchcommittee" },
      intersocietycommittee: {
        main: "committees",
        sub: "intersocietycommittee",
      },
      committeemembers: { main: "committees", sub: "committeemembers" },
      comingsoon: { main: "comingsoon" },
      members: { main: "members", sub: "members" },
      fiestasessions: { main: "resources", sub: "fiestasessions" },
      privacypolicy: { main: "privacypolicy" },
      cancelpolicy: { main: "cancelpolicy" },
    };

    // Update active menus based on location - keep this part
    switch (true) {
      case has(menuActiveMap, locName):
        setActiveMenus(get(menuActiveMap, [locName, "main"]));
        if (has(menuActiveMap, [locName, "sub"])) {
          setActiveSubMenus(get(menuActiveMap, [locName, "sub"]));
        }
        break;
      default:
        setActiveMenus(locName);
        break;
    }
  }, []); // Empty dependency array - only run once

  return (
    <div className="main-section">
      <div className="aside-sec aside aside-fixed admin-fixed">
        <div className="aside-inner">
          <div className="aside-menu">
            <ul>
              <li>
                <div
                  className={`menu-sec ${activeMenus === "home" ? "menu-active" : ""}`}
                  onClick={(e) => handleclickmenu("home", true)}
                >
                  <span>
                    <HomeIcon />
                  </span>
                  Home
                  <span className="arrow-right">
                    <NavigateNextIcon />
                  </span>
                </div>
                <ul
                  className={`sub-menu-sec ${activeMenus === "home" ? "submenudisblock " : "submenudisnone"}`}
                >
                  <li
                    className={`menu-sec ${activeSubMenus === "homebanner" ? "submenu-active" : ""}`}
                    onClick={(e) => handleclicksubmenu("homebanner")}
                  >
                    Row 1: Banner
                  </li>
                  <li
                    className={`menu-sec ${activeSubMenus === "join" ? "submenu-active" : ""}`}
                    onClick={(e) => handleclicksubmenu("join")}
                  >
                    Row 2: L/R Boxes
                  </li>
                  <li
                    className={`menu-sec ${activeSubMenus === "mission" ? "submenu-active" : ""}`}
                    onClick={(e) => handleclicksubmenu("mission")}
                  >
                    Row 3: Our Mission
                  </li>
                  {/*<li  className={`menu-sec ${activeSubMenus === 'news' ? 'submenu-active' : ''}`} onClick={(e) => handleclicksubmenu('news')}>News</li> */}
                  <li
                    className={`menu-sec ${activeSubMenus === "getinvolve" ? "submenu-active" : ""}`}
                    onClick={(e) => handleclicksubmenu("getinvolve")}
                  >
                    Row 4: Getting Involved
                  </li>
                </ul>
              </li>
              <li>
                <div
                  className={`menu-sec ${activeMenus === "about" ? "menu-active" : ""}`}
                  onClick={(e) => handleclickmenu("about", true)}
                >
                  <span>
                    <InfoIcon />
                  </span>
                  About
                  <span className="arrow-right">
                    <NavigateNextIcon />
                  </span>
                </div>
                <ul
                  className={`sub-menu-sec ${activeMenus === "about" ? "submenudisblock " : "submenudisnone"}`}
                >
                  <li
                    className={`menu-sec ${activeSubMenus === "aboutbanner" ? "submenu-active" : ""}`}
                    onClick={(e) => handleclicksubmenu("aboutbanner")}
                  >
                    Banner
                  </li>
                  <li
                    className={`menu-sec ${activeSubMenus === "contentmanagement" ? "submenu-active" : ""}`}
                    onClick={(e) => handleclicksubmenu("contentmanagement")}
                  >
                    Content
                  </li>
                  <li
                    className={`menu-sec ${activeSubMenus === "directors" ? "submenu-active" : ""}`}
                    onClick={(e) => handleclicksubmenu("directors")}
                  >
                    Board of Directors
                  </li>
                  {/*<li className={`menu-sec ${activeSubMenus === 'story' ? 'submenu-active' : ''}`} onClick={(e) => handleclicksubmenu('story')}>Story</li>*/}
                </ul>
              </li>

              <li>
                <div
                  className={`menu-sec ${activeMenus === "committees" ? "menu-active" : ""}`}
                  onClick={(e) => handleclickmenu("committees", true)}
                >
                  <span>
                    <GroupIcon />
                  </span>
                  Committees
                  <span className="arrow-right">
                    <NavigateNextIcon />
                  </span>
                </div>
                <ul
                  className={`sub-menu-sec ${activeMenus === "committees" ? "submenudisblock " : "submenudisnone"}`}
                >
                  <li
                    className={`menu-sec ${activeSubMenus === "educationmeetingplanningcommittee" ? "submenu-active" : ""}`}
                    onClick={(e) =>
                      handleclicksubmenu("educationmeetingplanningcommittee")
                    }
                  >
                    Education and Meeting Planning Committee
                  </li>
                  <li
                    className={`menu-sec ${activeSubMenus === "financemembershipcommittee" ? "submenu-active" : ""}`}
                    onClick={(e) =>
                      handleclicksubmenu("financemembershipcommittee")
                    }
                  >
                    Finance Membership Committee
                  </li>
                  <li
                    className={`menu-sec ${activeSubMenus === "researchcommittee" ? "submenu-active" : ""}`}
                    onClick={(e) => handleclicksubmenu("researchcommittee")}
                  >
                    Research Committee
                  </li>
                  <li
                    className={`menu-sec ${activeSubMenus === "intersocietycommittee" ? "submenu-active" : ""}`}
                    onClick={(e) => handleclicksubmenu("intersocietycommittee")}
                  >
                    Intersociety Committee
                  </li>
                  <li
                    className={`menu-sec ${activeSubMenus === "committeemembers" ? "submenu-active" : ""}`}
                    onClick={(e) => handleclicksubmenu("committeemembers")}
                  >
                    Committee Members
                  </li>
                </ul>
              </li>
              {/* <li>
                <div
                  className={`menu-sec ${activeMenus === "affiliates" ? "menu-active" : ""}`}
                  onClick={(e) => handleclickmenu("affiliates", true)}
                >
                  <span>
                    <GroupIcon />
                  </span>
                  Affiliates
                </div>
              </li> */}
              <li>
                <div
                  className={`menu-sec ${activeMenus === "membership" ? "menu-active" : ""}`}
                  onClick={(e) => handleclickmenu("membership", true)}
                >
                  <span>
                    <i className="fa-solid fa-user-check"></i>
                  </span>
                  Membership
                  <span className="arrow-right">
                    <NavigateNextIcon />
                  </span>
                </div>
                <ul
                  className={`sub-menu-sec ${activeMenus === "membership" ? "submenudisblock " : "submenudisnone"}`}
                >
                  <li
                    className={`menu-sec ${activeSubMenus === "members" ? "submenu-active" : ""}`}
                    onClick={(e) => handleclicksubmenu("members")}
                  >
                    Members
                  </li>
                  <li
                    className={`menu-sec ${activeSubMenus === "benefitsmembership" ? "submenu-active" : ""}`}
                    onClick={(e) => handleclicksubmenu("benefitsmembership")}
                  >
                    Benefits Of Membership
                  </li>
                  {/* <li className={`menu-sec ${activeSubMenus === 'memberhighlight' ? 'submenu-active' : ''}`} onClick={(e) => handleclicksubmenu('memberhighlight')}>Member Highlight</li> */}
                  <li
                    className={`menu-sec ${activeSubMenus === "membershipcategories" ? "submenu-active" : ""}`}
                    onClick={(e) => handleclicksubmenu("membershipcategories")}
                  >
                    Membership Categories
                  </li>
                  {/* <li className={`menu-sec ${activeSubMenus === 'printableapplication' ? 'submenu-active' : ''}`} onClick={(e) => handleclicksubmenu('printableapplication')}>Printable application</li>
                                <li className={`menu-sec ${activeSubMenus === 'renewmembership' ? 'submenu-active' : ''}`} onClick={(e) => handleclicksubmenu('renewmembership')}>Renew Membership</li> */}
                  <li
                    className={`menu-sec ${activeSubMenus === "getinvolvedmembership" ? "submenu-active" : ""}`}
                    onClick={(e) => handleclicksubmenu("getinvolvedmembership")}
                  >
                    Get Involved
                  </li>
                </ul>
              </li>
              <li>
                <div
                  className={`menu-sec ${activeMenus === "meeting" ? "menu-active" : ""}`}
                  onClick={(e) => handleclickmenu("meeting", true)}
                >
                  <span>
                    <GroupIcon />
                  </span>
                  Meeting
                  <span className="arrow-right">
                    <NavigateNextIcon />
                  </span>
                </div>
                <ul
                  className={`sub-menu-sec ${activeMenus === "meeting" ? "submenudisblock " : "submenudisnone"}`}
                >
                  <li
                    className={`menu-sec ${activeSubMenus === "meeting-registrants" ? "submenu-active" : ""}`}
                    onClick={(e) => handleclicksubmenu("meeting-registrants")}
                  >
                    Meeting Registrants
                  </li>
                  <li
                    className={`menu-sec ${activeSubMenus === "meetings" ? "submenu-active" : ""}`}
                    onClick={(e) => handleclicksubmenu("meetings")}
                  >
                    Meeting Management
                  </li>
                  <li
                    className={`menu-sec ${activeSubMenus === "meetingbanner" ? "submenu-active" : ""}`}
                    onClick={(e) => handleclicksubmenu("meetingbanner")}
                  >
                    Banner
                  </li>
                </ul>
              </li>
              <li>
                <div
                  className={`menu-sec ${activeMenus === "resources" ? "menu-active" : ""}`}
                  onClick={(e) => handleclickmenu("resources", true)}
                >
                  <span>
                    <GroupIcon />
                  </span>
                  Resources
                  <span className="arrow-right">
                    <NavigateNextIcon />
                  </span>
                </div>
                <ul
                  className={`sub-menu-sec ${activeMenus === "resources" ? "submenudisblock " : "submenudisnone"}`}
                >
                  <li
                    className={`menu-sec ${activeSubMenus === "fiestasessions" ? "submenu-active" : ""}`}
                    onClick={(e) => handleclicksubmenu("fiestasessions")}
                  >
                    Fiesta Sessions
                  </li>
                </ul>
              </li>
              <li onClick={(e) => handleclickmenu("patients", true)}>
                <div
                  className={`menu-sec ${activeMenus === "patients" ? "menu-active" : ""}`}
                >
                  <span>
                    <GroupIcon />
                  </span>
                  Patients
                </div>
              </li>
              <li onClick={(e) => handleclickmenu("privacypolicy", true)}>
                <div
                  className={`menu-sec ${activeMenus === "privacypolicy" ? "menu-active" : ""}`}
                >
                  <span>
                    <GroupIcon />
                  </span>
                  Privacy Policy
                </div>
              </li>
              <li onClick={(e) => handleclickmenu("cancelpolicy", true)}>
                <div
                  className={`menu-sec ${activeMenus === "cancelpolicy" ? "menu-active" : ""}`}
                >
                  <span>
                    <GroupIcon />
                  </span>
                  Cancel Policy
                </div>
              </li>
              {/* <li onClick={(e) => handleclickmenu("contact", true)}>
                <div
                  className={`menu-sec ${activeMenus === "contact" ? "menu-active" : ""}`}
                >
                  <span>
                    <CallIcon />
                  </span>
                  Contact Us
                </div>
              </li> */}
              <li onClick={(e) => handleclickmenu("site", true)}>
                <div
                  className={`menu-sec ${activeMenus === "site" ? "menu-active" : ""}`}
                >
                  <span>
                    <InsertDriveFileIcon />
                  </span>
                  Site
                </div>
              </li>
              <li onClick={(e) => handleclickmenu("comingsoon", true)}>
                <div
                  className={`menu-sec ${activeMenus === "comingsoon" ? "menu-active" : ""}`}
                >
                  <span>
                    <AccessTimeIcon />
                  </span>
                  Coming Soon
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Sidebar;
